import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Collapse from '@mui/material/Collapse';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FilterListIcon from '@mui/icons-material/FilterList';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import ToggleButton from '@mui/material/ToggleButton';
import TableBox from '../../../components/TableBox';
import Button from '../../../components/Button';
import Input from '../../../components/Input';
import CustomizedSwitch from '../../../components/Switch';
import Toast from '../../../components/Toast';
import BulkActionBar from '../../../components/BulkActionBar';
import BulkSuspendModal from '../../../components/bulk/BulkSuspendModal';
import useFetch from '../../../hooks/useFetch';
import usePagination from '../../../hooks/usePagination';
import useSorting from '../../../hooks/useSorting';
import { usePermission } from '../../../hooks/usePermission';
import TenantService from '../../../services/tenant.service';
import ProductService from '../../../services/product.service';
import PlanService from '../../../services/plan.service';
import MasterService from '../../../services/master.service';
import MasterLookupService from '../../../services/master-lookup.service';
import SavedFilterService, { SavedFilter } from '../../../services/saved-filter.service';
import { HeadCellType, TableContextProvider } from '../../../interfaces/common';
import { SortOrder, Modules, RouteParamsTypes } from '../../../enums';
import { sortData } from '../../../utils/sortUtils';
import TenantRowContent from './TenantRow';

const SAVED_FILTER_NAMESPACE = 'tenant-list';
const MAX_SAVED_FILTERS = 10;
const SEARCH_DEBOUNCE_MS = 300;

const SUBSCRIPTION_STATES = ['active', 'expiring_soon', 'expired', 'trial'] as const;

interface TenantRow {
  id: number;
  tenant_code: string;
  tenant_name: string;
  slug: string;
  product_code: string | null;
  plan_name: string | null;
  status_name: string | null;
  status_code: string | null;
  status_colour: string | null;
  isolation_mode: 'shared' | 'dedicated';
  owner_email: string;
  country_name: string | null;
  subscription_start_date: string;
}

interface Option {
  id: number;
  label: string;
}

interface FilterState {
  status: number[];
  product: number[];
  plan: number[];
  country: number[];
  subscriptionState: string[];
}

const EMPTY_FILTERS: FilterState = { status: [], product: [], plan: [], country: [], subscriptionState: [] };

function parseIdList(value: string | null): number[] {
  if (!value) return [];
  return value
    .split(',')
    .map(Number)
    .filter((n) => !Number.isNaN(n));
}

const TenantList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const canEdit = usePermission('tenant:edit');
  const canDelete = usePermission('tenant:delete');

  const { page, perPage, setPage, setPerPage } = usePagination({ defaultPage: 1, defaultPerPage: 10 });
  const { order, orderBy, setOrder, setOrderBy } = useSorting({ defaultOrder: SortOrder.desc, defaultOrderBy: 'id' });

  const [items, setItems] = useState<TenantRow[]>([]);
  const [total, setTotal] = useState(0);
  const [showArchived, setShowArchived] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState<FilterState>(() => ({
    status: parseIdList(searchParams.get('status')),
    product: parseIdList(searchParams.get('product')),
    plan: parseIdList(searchParams.get('plan')),
    country: parseIdList(searchParams.get('country')),
    subscriptionState: searchParams.get('subscriptionState')?.split(',').filter(Boolean) ?? [],
  }));
  const [searchInput, setSearchInput] = useState(searchParams.get('search') ?? '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get('search') ?? '');
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [statusOptions, setStatusOptions] = useState<Option[]>([]);
  const [statusColours, setStatusColours] = useState<Record<number, string>>({});
  const [productOptions, setProductOptions] = useState<Option[]>([]);
  const [planOptions, setPlanOptions] = useState<Option[]>([]);
  const [countryOptions, setCountryOptions] = useState<Option[]>([]);

  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveFilterName, setSaveFilterName] = useState('');
  const [saving, setSaving] = useState(false);

  // US-052: multi-select mode + bulk actions
  const canSuspend = usePermission('tenant:status-transition');
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [selectAllFilter, setSelectAllFilter] = useState(false);
  const [suspendModalOpen, setSuspendModalOpen] = useState(false);

  // Filter option sources — fetched once, product-scoped by the backend already.
  useEffect(() => {
    MasterService.list({ type: 'tenantStatuses', limit: 100 }).then((response: any) => {
      const rows = response?.data?.items ?? [];
      setStatusOptions(rows.map((r: any) => ({ id: r.id, label: r.status_name })));
      setStatusColours(Object.fromEntries(rows.map((r: any) => [r.id, r.colour_code])));
    });
    ProductService.list({ includeDeleted: false, limit: 100 }).then((response: any) => {
      const rows = response?.data?.items ?? [];
      setProductOptions(rows.map((r: any) => ({ id: r.id, label: r.product_name })));
    });
    MasterLookupService.list({ type: 'countries', limit: 100 }).then((response: any) => {
      const rows = response?.data?.items ?? [];
      setCountryOptions(rows.map((r: any) => ({ id: r.id, label: r.country_name })));
    });
    SavedFilterService.list(SAVED_FILTER_NAMESPACE).then((response: any) => {
      setSavedFilters(response?.data ?? []);
    });
  }, []);

  // Plan options re-filter when the Product filter changes (AC3).
  useEffect(() => {
    const productId = filters.product.length === 1 ? filters.product[0] : undefined;
    PlanService.list({ includeDeleted: false, limit: 100, productId }).then((response: any) => {
      const rows = response?.data?.items ?? [];
      setPlanOptions(rows.map((r: any) => ({ id: r.id, label: r.plan_name })));
    });
  }, [filters.product]);

  // Debounced text search (AC4): 300ms after the last keystroke.
  useEffect(() => {
    setSearching(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setSearching(false);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  // AC5: filter state is the URL — every filter/search/page change updates query params.
  useEffect(() => {
    const params: Record<string, string> = {};
    if (filters.status.length) params.status = filters.status.join(',');
    if (filters.product.length) params.product = filters.product.join(',');
    if (filters.plan.length) params.plan = filters.plan.join(',');
    if (filters.country.length) params.country = filters.country.join(',');
    if (filters.subscriptionState.length) params.subscriptionState = filters.subscriptionState.join(',');
    if (debouncedSearch) params.search = debouncedSearch;
    if (page > 1) params.page = String(page);
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, debouncedSearch, page]);

  const { loading } = useFetch({
    service: TenantService,
    func: 'list',
    payload: {
      showArchived,
      page,
      limit: perPage,
      status: filters.status,
      product: filters.product,
      plan: filters.plan,
      country: filters.country,
      subscriptionState: filters.subscriptionState as any,
      search: debouncedSearch || undefined,
    },
    dependency: [showArchived, page, perPage, filters, debouncedSearch],
    onSuccess: (data: any) => {
      setItems(data?.items ?? []);
      setTotal(data?.pagination?.total ?? 0);
    },
    onError: () => {
      setItems([]);
      setTotal(0);
    },
  });

  /** US-052: "select all matching filter" sends the same filter shape the bulk endpoint's resolveTenantIds() parses. */
  const bulkFilterParams = useMemo(() => {
    const params: Record<string, string> = {};
    if (filters.status.length) params.status = filters.status.join(',');
    if (filters.product.length) params.product = filters.product.join(',');
    if (filters.plan.length) params.plan = filters.plan.join(',');
    if (filters.country.length) params.country = filters.country.join(',');
    if (filters.subscriptionState.length) params.subscriptionState = filters.subscriptionState.join(',');
    if (debouncedSearch) params.search = debouncedSearch;
    return params;
  }, [filters, debouncedSearch]);

  const handleFilterChange = useCallback((key: keyof FilterState, values: number[] | string[]) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [key]: values }));
  }, [setPage]);

  const handleClearFilters = useCallback(() => {
    setPage(1);
    setFilters(EMPTY_FILTERS);
    setSearchInput('');
    setDebouncedSearch('');
  }, [setPage]);

  const applySavedFilter = useCallback(
    (saved: SavedFilter) => {
      setPage(1);
      const f = saved.filters as Partial<FilterState & { search: string }>;
      setFilters({
        status: f.status ?? [],
        product: f.product ?? [],
        plan: f.plan ?? [],
        country: f.country ?? [],
        subscriptionState: f.subscriptionState ?? [],
      });
      setSearchInput(f.search ?? '');
      setDebouncedSearch(f.search ?? '');
    },
    [setPage]
  );

  const handleDeleteSavedFilter = useCallback(
    async (id: string) => {
      await SavedFilterService.remove(SAVED_FILTER_NAMESPACE, id);
      setSavedFilters((prev) => prev.filter((f) => f.id !== id));
      toast.success(<Toast message={t('tenants.list.pageTexts.savedFilters.deleteSuccess')} />, { icon: false });
    },
    [t]
  );

  const handleSaveFilter = useCallback(async () => {
    if (!saveFilterName.trim()) return;
    setSaving(true);
    try {
      const response: any = await SavedFilterService.create(SAVED_FILTER_NAMESPACE, saveFilterName.trim(), {
        ...filters,
        search: debouncedSearch,
      });
      setSavedFilters((prev) => [...prev, response.data]);
      setSaveDialogOpen(false);
      setSaveFilterName('');
      toast.success(<Toast message={t('tenants.list.pageTexts.savedFilters.saveSuccess')} />, { icon: false });
    } catch (err) {
      // interceptor already surfaces the error toast (e.g. limit reached)
    } finally {
      setSaving(false);
    }
  }, [filters, debouncedSearch, saveFilterName, t]);

  // US-052 ST-004: multi-select mode handlers
  const handleToggleSelectMode = useCallback(() => {
    setSelectMode((v) => !v);
    setSelectedIds(new Set());
    setSelectAllFilter(false);
  }, []);

  const handleToggleSelect = useCallback((id: number) => {
    setSelectAllFilter(false);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleSelectAllPage = useCallback(() => {
    setSelectAllFilter(false);
    setSelectedIds(new Set(items.map((item) => item.id)));
  }, [items]);

  const handleSelectAllFilter = useCallback(() => {
    setSelectAllFilter(true);
    setSelectedIds(new Set(items.map((item) => item.id)));
  }, [items]);

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setSelectAllFilter(false);
  }, []);

  const headCells: HeadCellType[] = useMemo(
    () => [
      ...(selectMode ? [{ id: 'select', sortEnabled: false, width: '4%', label: '' }] : []),
      { id: 'tenant_code', sortEnabled: true, width: '10%', label: t('tenants.list.pageTexts.table.columns.code') },
      { id: 'tenant_name', sortEnabled: true, width: '16%', label: t('tenants.list.pageTexts.table.columns.name') },
      { id: 'product_code', sortEnabled: true, width: '10%', label: t('tenants.list.pageTexts.table.columns.product') },
      { id: 'plan_name', sortEnabled: true, width: '10%', label: t('tenants.list.pageTexts.table.columns.plan') },
      { id: 'status', sortEnabled: true, width: '10%', label: t('tenants.list.pageTexts.table.columns.status') },
      { id: 'owner_email', sortEnabled: true, width: '14%', label: t('tenants.list.pageTexts.table.columns.ownerEmail') },
      { id: 'country_name', sortEnabled: true, width: '10%', label: t('tenants.list.pageTexts.table.columns.country') },
      { id: 'subscription_start_date', sortEnabled: true, width: '10%', label: t('tenants.list.pageTexts.table.columns.startDate') },
      { id: 'actions', sortEnabled: false, width: '10%', label: t('tenants.list.pageTexts.table.columns.actions') },
    ],
    [t, selectMode]
  );

  const sortedItems = useMemo(() => sortData(items, orderBy, order), [items, orderBy, order]);

  const Row = useCallback(
    ({ item, className }: { item: TenantRow; className?: string }) => (
      <TenantRowContent
        item={item}
        className={className}
        canEdit={canEdit}
        canDelete={canDelete}
        selectMode={selectMode}
        selected={selectedIds.has(item.id)}
        onToggleSelect={handleToggleSelect}
      />
    ),
    [canEdit, canDelete, selectMode, selectedIds, handleToggleSelect]
  );

  const values: TableContextProvider = useMemo(
    () => ({
      headCells,
      row: Row,
      order,
      orderBy,
      page,
      perPage,
      total,
      loading,
      data: sortedItems,
      pagination: true,
      noDataFoundMessage: t('tenants.list.pageTexts.table.noDataFoundMessage'),
      setPage,
      setPerPage,
      setOrder,
      setOrderBy,
    }),
    [headCells, Row, order, orderBy, page, perPage, total, loading, sortedItems, t, setPage, setPerPage, setOrder, setOrderBy]
  );

  const toOptionValues = (options: Option[], ids: number[]) => options.filter((o) => ids.includes(o.id));

  return (
    <>
      <div className="d-flex justify-content-between align-items-end mb-4">
        <div>
          <span className="obs-eyebrow d-block mb-1">
            {t('tenants.list.pageTexts.eyebrow') || 'TENANT MANAGEMENT & DIRECTORY'}
          </span>
          <h2 className="obs-page-title mb-0">{t('tenants.list.pageTexts.title') || 'Tenants.'}</h2>
        </div>
        <div className="d-flex align-items-center gap-4">
          <CustomizedSwitch
            label={t('tenants.list.pageTexts.showArchived')}
            name="showArchived"
            checked={showArchived}
            onChange={() => setShowArchived((v) => !v)}
          />
          <ToggleButton
            value="select"
            size="small"
            selected={selectMode}
            onChange={handleToggleSelectMode}
          >
            {t('tenants.list.pageTexts.bulk.selectToggle')}
          </ToggleButton>
          <Button
            className="btn btn-primary"
            text={t('tenants.list.pageTexts.addNew')}
            onClick={() => navigate(`${Modules.tenants}${RouteParamsTypes.add}`)}
          />
        </div>
      </div>

      {selectMode && (
        <div className="d-flex gap-3 mb-2">
          <button type="button" className="btn btn-sm btn-link p-0" onClick={handleSelectAllPage}>
            {t('tenants.list.pageTexts.bulk.selectAllPage')}
          </button>
          <button type="button" className="btn btn-sm btn-link p-0" onClick={handleSelectAllFilter}>
            {t('tenants.list.pageTexts.bulk.selectAllFilter', { count: total })}
          </button>
        </div>
      )}

      <BulkActionBar
        selectedCount={selectedIds.size}
        canSuspend={canSuspend}
        onSuspend={() => setSuspendModalOpen(true)}
        onClear={handleClearSelection}
      />

      {savedFilters.length > 0 && (
        <div className="d-flex flex-wrap gap-2 mb-3">
          {savedFilters.map((saved) => (
            <Chip
              key={saved.id}
              label={saved.name}
              onClick={() => applySavedFilter(saved)}
              onDelete={() => handleDeleteSavedFilter(saved.id)}
            />
          ))}
        </div>
      )}

      <div className="d-flex align-items-center gap-2 mb-3">
        <div className="flex-grow-1">
          <Input
            id="tenant-search"
            name="search"
            placeholder={t('tenants.list.pageTexts.searchPlaceholder')}
            value={searchInput}
            onChange={(e: any) => setSearchInput(e.target.value)}
          />
        </div>
        {searching && <span className="spinner-border spinner-border-sm text-muted" />}
        <IconButton onClick={() => setFiltersOpen((v) => !v)} color={filtersOpen ? 'primary' : 'default'}>
          <FilterListIcon />
        </IconButton>
        <IconButton
          onClick={() => setSaveDialogOpen(true)}
          disabled={savedFilters.length >= MAX_SAVED_FILTERS}
          title={
            savedFilters.length >= MAX_SAVED_FILTERS
              ? t('tenants.list.pageTexts.savedFilters.limitReached')
              : t('tenants.list.pageTexts.savedFilters.save')
          }
        >
          <BookmarkAddIcon />
        </IconButton>
      </div>

      <Collapse in={filtersOpen}>
        <div className="row g-3 mb-3">
          <div className="col-md-3">
            <Autocomplete
              multiple
              size="small"
              options={statusOptions}
              value={toOptionValues(statusOptions, filters.status)}
              onChange={(_e, value) => handleFilterChange('status', value.map((v) => v.id))}
              getOptionLabel={(o) => o.label}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <span
                    className="rounded-circle d-inline-block me-2"
                    style={{ width: 10, height: 10, backgroundColor: statusColours[option.id] ?? undefined }}
                  />
                  {option.label}
                </li>
              )}
              renderInput={(params) => <TextField {...params} label={t('tenants.list.pageTexts.filters.status')} />}
            />
          </div>
          <div className="col-md-3">
            <Autocomplete
              multiple
              size="small"
              options={productOptions}
              value={toOptionValues(productOptions, filters.product)}
              onChange={(_e, value) => handleFilterChange('product', value.map((v) => v.id))}
              getOptionLabel={(o) => o.label}
              renderInput={(params) => <TextField {...params} label={t('tenants.list.pageTexts.filters.product')} />}
            />
          </div>
          <div className="col-md-3">
            <Autocomplete
              multiple
              size="small"
              options={planOptions}
              value={toOptionValues(planOptions, filters.plan)}
              onChange={(_e, value) => handleFilterChange('plan', value.map((v) => v.id))}
              getOptionLabel={(o) => o.label}
              renderInput={(params) => <TextField {...params} label={t('tenants.list.pageTexts.filters.plan')} />}
            />
          </div>
          <div className="col-md-3">
            <Autocomplete
              multiple
              size="small"
              options={countryOptions}
              value={toOptionValues(countryOptions, filters.country)}
              onChange={(_e, value) => handleFilterChange('country', value.map((v) => v.id))}
              getOptionLabel={(o) => o.label}
              renderInput={(params) => <TextField {...params} label={t('tenants.list.pageTexts.filters.country')} />}
            />
          </div>
          <div className="col-md-3">
            <Autocomplete
              multiple
              size="small"
              options={SUBSCRIPTION_STATES as unknown as string[]}
              value={filters.subscriptionState}
              onChange={(_e, value) => handleFilterChange('subscriptionState', value as string[])}
              getOptionLabel={(o) => t(`tenants.list.pageTexts.filters.subscriptionStates.${o}`)}
              renderInput={(params) => (
                <TextField {...params} label={t('tenants.list.pageTexts.filters.subscriptionState')} />
              )}
            />
          </div>
          <div className="col-md-3 d-flex align-items-center">
            <Button
              className="btn btn-outline-secondary"
              text={t('tenants.list.pageTexts.filters.clearAll')}
              onClick={handleClearFilters}
            />
          </div>
        </div>
      </Collapse>

      <TableBox tableValues={values} />

      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
        <DialogTitle>{t('tenants.list.pageTexts.savedFilters.namePrompt')}</DialogTitle>
        <DialogContent>
          <Input
            id="save-filter-name"
            name="saveFilterName"
            label={t('tenants.list.pageTexts.savedFilters.nameLabel')}
            value={saveFilterName}
            onChange={(e: any) => setSaveFilterName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            className="btn btn-outline-secondary"
            text={t('tenants.form.pageTexts.buttons.cancel')}
            onClick={() => setSaveDialogOpen(false)}
          />
          <Button
            className="btn btn-primary"
            text={t('tenants.list.pageTexts.savedFilters.save')}
            loading={saving}
            disabled={!saveFilterName.trim()}
            onClick={handleSaveFilter}
          />
        </DialogActions>
      </Dialog>

      <BulkSuspendModal
        open={suspendModalOpen}
        onClose={() => {
          setSuspendModalOpen(false);
          handleClearSelection();
        }}
        selectedTenants={items
          .filter((item) => selectedIds.has(item.id))
          .map((item) => ({
            id: item.id,
            tenant_name: item.tenant_name,
            status_code: item.status_code,
            status_name: item.status_name,
          }))}
        selectAllFilter={selectAllFilter}
        filterParams={bulkFilterParams}
        matchingCount={total}
      />
    </>
  );
};

export default TenantList;
